import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default branding (Trader Titans)
const DEFAULT_BRANDING = {
  company_name: 'Trader Titans',
  tagline: 'The Fantasy Trading Game for Financial Markets',
  logo_light_url: null,
  logo_dark_url: null,
  favicon_url: null,
  primary_color: '#10B981',
  secondary_color: '#1E293B',
  accent_color: '#F59E0B',
  email_from_name: 'Trader Titans'
};

const DEFAULT_SETTINGS = {
  allow_registration: true,
  default_virtual_cash: 100000,
  max_instruments: 10,
  enable_pro_tier: true,
  enable_affiliate: true,
  enable_cosmetics: true,
  enable_leagues: true
};

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    // Return defaults if no provider (shouldn't happen in normal use)
    return {
      tenant: null,
      branding: DEFAULT_BRANDING,
      settings: DEFAULT_SETTINGS,
      loading: false,
      isWhiteLabel: false
    };
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTenantConfig();
  }, []);

  const fetchTenantConfig = async () => {
    try {
      // Get tenant from URL query param if present
      const urlParams = new URLSearchParams(window.location.search);
      const tenantSlug = urlParams.get('tenant');
      
      // Build URL with tenant param if present
      let url = `${API_URL}/api/tenant/config`;
      if (tenantSlug) {
        url += `?tenant=${tenantSlug}`;
      }
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setTenant(data);
        applyBranding(data.branding);
        applyFavicon(data.branding?.favicon_url);
        updateDocumentTitle(data.branding?.company_name);
      } else {
        // Use defaults if tenant config fails
        console.warn('Failed to fetch tenant config, using defaults');
        applyBranding(DEFAULT_BRANDING);
      }
    } catch (err) {
      console.error('Error fetching tenant config:', err);
      setError(err.message);
      applyBranding(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  };

  const applyBranding = (branding) => {
    if (!branding) return;

    const root = document.documentElement;
    
    // Apply CSS custom properties for theming
    if (branding.primary_color) {
      root.style.setProperty('--tenant-primary', branding.primary_color);
      root.style.setProperty('--tenant-primary-rgb', hexToRgb(branding.primary_color));
    }
    
    if (branding.secondary_color) {
      root.style.setProperty('--tenant-secondary', branding.secondary_color);
      root.style.setProperty('--tenant-secondary-rgb', hexToRgb(branding.secondary_color));
    }
    
    if (branding.accent_color) {
      root.style.setProperty('--tenant-accent', branding.accent_color);
      root.style.setProperty('--tenant-accent-rgb', hexToRgb(branding.accent_color));
    }
  };

  const applyFavicon = (faviconUrl) => {
    if (!faviconUrl) return;
    
    // Find existing favicon or create new one
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  };

  const updateDocumentTitle = (companyName) => {
    if (companyName && companyName !== 'Trader Titans') {
      // Update base title for white-label
      document.title = companyName;
    }
  };

  // Helper to convert hex to RGB values
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
    }
    return '16, 185, 129'; // Default emerald
  };

  const branding = tenant?.branding || DEFAULT_BRANDING;
  const settings = tenant?.settings_public || DEFAULT_SETTINGS;
  const isWhiteLabel = tenant?.slug && tenant.slug !== 'default';

  const value = {
    tenant,
    branding,
    settings,
    loading,
    error,
    isWhiteLabel,
    refreshTenant: fetchTenantConfig
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
